import { Injectable , inject} from '@angular/core';
import { AppConfigService } from 'app/core/config/app-config.service';
import { HttpClient } from '@angular/common/http';
import { of, Observable, from } from 'rxjs';
import { tap, switchMap } from 'rxjs/operators';
import { DynamicConfigService } from '@app/core/config/dynamic-config.service';

import { environment } from '@env/environment';

@Injectable({ providedIn: 'root' })
export class WordpressAuthService {
  readonly appConfig = inject(AppConfigService);
  private token: string | null = null;
  private wordpressUrl: string = '';
  private wpUsername: string = '';
  private wpPassword: string = '';
  /** Site + account the current token was issued for. */
  private tokenOwner = '';

  constructor(
    private http: HttpClient,
    private configService: DynamicConfigService
  ) {}

  /**
   * Re-reads the credentials for the logged-in user's company (cached by DynamicConfigService)
   * and drops the token when they belong to a different site or account.
   */
  async sync(): Promise<void> {
    await this.initializeConfig();
    const owner = `${this.wordpressUrl}|${this.wpUsername}`;
    if (owner !== this.tokenOwner) {
      this.token = null;
      this.tokenOwner = owner;
    }
  }

  private async initializeConfig(): Promise<void> {
    try {
      const wpConfig = await this.configService.getWordpressConfig();
      this.wordpressUrl = this.resolveWpUrl(wpConfig.wpurl);
      this.wpUsername = wpConfig.wpu;
      this.wpPassword = wpConfig.wpt;
      console.log('WordpressAuthService initialized with URL:', this.wordpressUrl);
    } catch (error) {
      console.error('Failed to initialize WordpressAuthService config:', error);
      this.wordpressUrl = this.resolveWpUrl(this.appConfig.get('fallback_wpurl'));
      this.wpUsername = '';
      this.wpPassword = '';
    }
  }

  /**
   * In local development (localhost), route WordPress requests through the Angular dev-server
   * proxy (/kdb-proxy) to avoid browser CORS restrictions.
   * In production the full URL is used directly.
   */
  private resolveWpUrl(fullUrl: string): string {
    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (!isLocalDev) {
      return fullUrl.replace(/\/+$/, '');
    }
    // Extract the path portion after the host (e.g. "/studema/ontarget")
    try {
      const parsed = new URL(fullUrl);
      const path = parsed.pathname.replace(/\/+$/, '');
      return `/kdb-proxy${path}`;
    } catch {
      return fullUrl.replace(/\/+$/, '');
    }
  }

  getToken(): string | null {
    return this.token;
  }

  isAuthenticated(): boolean {
    return !!this.token;
  }

  login(): Observable<any> {
    return from(this.sync()).pipe(
      switchMap(() => {
        const base = this.wordpressUrl.replace(/\/+$/, '');
        const url = `${base}/wp-json/jwt-auth/v1/token`;
        const body = {
          username: this.rot13_decode(this.wpUsername),
          password: this.rot13_decode(this.wpPassword),
        };
        return this.http.post(url, body).pipe(
          tap((res: any) => { this.token = res.token; })
        );
      })
    );
  }

  rot13_decode(str: string): string {
    return str.replace(/[a-zA-Z]/g, (c) => String.fromCharCode(c.charCodeAt(0) + (c.toLowerCase() < 'n' ? 13 : -13)));
  }
}
